import { useParams, Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Check,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import { LOCATIONS } from "@/data/siteData";
import { CTABanner } from "@/components/CTABanner";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

export default function LocationDetailPage() {
  const { locationId } = useParams();
  const location = LOCATIONS.find((l) => l.id === locationId);

  if (!location) {
    return (
      <div data-testid="location-not-found" className="py-32 text-center">
        <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
          Location not found
        </h2>
        <Link
          to="/locations"
          className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-600 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Locations
        </Link>
      </div>
    );
  }

  // Not every center has full content yet — fall back to what the card already shows.
  const gallery = location.gallery?.length ? location.gallery : [location.image];
  const email = location.email || "contact@coffice24x7.in";
  // A place CID points the embed at the real Google listing, so clicking the pin opens
  // its info card. Without one, a coordinate pin is all we can show — clicking it will
  // report "Place info couldn't load", since there is no place behind the point.
  const mapEmbed = location.placeCid
    ? `https://maps.google.com/maps?cid=${location.placeCid}&output=embed`
    : `https://maps.google.com/maps?q=${encodeURIComponent(
        location.coords
          ? `${location.coords.lat},${location.coords.lng}`
          : location.address
      )}&z=15&output=embed`;

  return (
    <div data-testid={`location-detail-${location.id}`}>
      {/* Header */}
      <section className="py-12 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/locations"
            data-testid="back-to-locations"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            All Locations
          </Link>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <h1
              data-testid="location-detail-heading"
              className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight"
            >
              {location.name}
            </h1>
            {location.premium && (
              <Badge className="bg-sky-600 text-white border-0">Premium</Badge>
            )}
          </div>
          <p className="text-base sm:text-lg text-slate-600 max-w-3xl font-body leading-relaxed">
            {location.fullDescription || location.shortDescription}
          </p>
        </div>
      </section>

      {/* Gallery */}
      <section data-testid="location-gallery" className="py-10 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`grid gap-4 ${
              gallery.length > 1 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1"
            }`}
          >
            {gallery.map((img, i) => (
              <div
                key={i}
                className={`rounded-2xl overflow-hidden shadow-lg ${
                  i === 0 && gallery.length > 1 ? "md:col-span-2 md:row-span-2" : ""
                }`}
              >
                <img
                  src={img}
                  alt={`${location.name} gallery ${i + 1}`}
                  className={`w-full object-cover ${
                    gallery.length === 1
                      ? "h-64 md:h-[28rem]"
                      : i === 0
                      ? "h-64 md:h-full"
                      : "h-48 md:h-64"
                  }`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities */}
      {location.amenities?.length > 0 && (
        <section data-testid="location-amenities" className="py-10 sm:py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-10">
              Amenities
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {location.amenities.map((amenity) => (
                <div
                  key={amenity}
                  data-testid={`amenity-${amenity.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-white rounded-xl p-4 border border-slate-100 flex items-center gap-3 shadow-sm"
                >
                  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-orange-500" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">{amenity}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Seat Types / Pricing */}
      <section data-testid="location-pricing" className="py-10 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-10">
            Plans & Pricing
          </h2>
          {location.seatTypes?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {location.seatTypes.map((seat, i) => (
                <div
                  key={seat.type}
                  data-testid={`seat-type-${i}`}
                  className={`rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                    i === 2
                      ? "border-orange-200 bg-orange-50 shadow-md"
                      : "border-slate-100 bg-white shadow-[0_4px_20px_rgb(0,0,0,0.03)]"
                  }`}
                >
                  {i === 2 && (
                    <Badge className="bg-orange-500 text-white border-0 mb-3">
                      Popular
                    </Badge>
                  )}
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">
                    {seat.type}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">{seat.description}</p>
                  <Separator className="mb-4" />
                  <div className="flex items-baseline gap-1">
                    <span className="font-heading text-3xl font-bold text-slate-900">
                      &#8377;{seat.price}
                    </span>
                    <span className="text-sm text-slate-500">{seat.period}</span>
                  </div>
                  <Link
                    to="/contact"
                    data-testid={`seat-enquire-${i}`}
                    className={`mt-6 block text-center rounded-full font-medium px-5 py-2.5 text-sm active:scale-95 transition-all ${
                      i === 2
                        ? "bg-orange-500 text-white hover:bg-orange-600 shadow-md shadow-orange-500/20"
                        : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                    }`}
                  >
                    Enquire Now
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div
              data-testid="location-pricing-on-request"
              className="rounded-2xl border border-slate-100 bg-white shadow-[0_4px_20px_rgb(0,0,0,0.03)] p-8 sm:p-10 text-center"
            >
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed max-w-xl mx-auto">
                Plans for our {location.name} center are tailored to your team size and
                how long you need the space. Tell us what you're looking for and we'll
                share current availability and pricing.
              </p>
              <Link
                to="/contact"
                data-testid="location-pricing-enquire"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-orange-500 text-white font-medium px-6 py-3 hover:bg-orange-600 active:scale-95 transition-all shadow-md shadow-orange-500/20"
              >
                Enquire Now
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Contact Info & Map */}
      <section data-testid="location-contact-map" className="py-10 sm:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight mb-8">
                Get in Touch
              </h2>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Address</p>
                    <p className="text-sm text-slate-600">{location.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Phone</p>
                    <a
                      href={`tel:${location.phone.replace(/\s/g, "")}`}
                      data-testid="location-phone"
                      className="text-sm text-slate-600 hover:text-orange-500 transition-colors"
                    >
                      {location.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Email</p>
                    <a
                      href={`mailto:${email}`}
                      data-testid="location-email"
                      className="text-sm text-slate-600 hover:text-orange-500 transition-colors"
                    >
                      {email}
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <Link
                  to="/contact"
                  data-testid="location-book-visit-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-orange-500 text-white font-medium px-6 py-3 hover:bg-orange-600 active:scale-95 transition-all shadow-md shadow-orange-500/20"
                >
                  Book a Visit
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            {/* Map */}
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg h-80 lg:h-auto">
              <iframe
                data-testid="location-map"
                src={mapEmbed}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "320px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`Map of Coffice 24x7 ${location.name}`}
              />
            </div>
          </div>
        </div>
      </section>

      <CTABanner
        title="Ready to Join?"
        subtitle={`Start working from our ${location.name} center today. Flexible plans, zero hassle.`}
        buttonText="Get a Call"
        buttonLink="/contact"
      />
    </div>
  );
}
